import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Autocomplete,
  TextField,
  Chip,
  Typography,
  Alert,
  MenuItem,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { useQuery } from "react-query";
import { useLocation } from "react-router-dom";

import { AuditService } from "../../../../services/audit.service";
import { useAllUsers } from "../../../../queries/hooks";
import ChangesList from "../ChangesList/ChangesList";
import { AUDIT_EVENT_OPTIONS } from "../auditEventOptions";

interface RequesterModalProps {
  isOpen: boolean;
  area: string;
  label: string;
  onClose: () => void;
  onImport?: (entries: any[]) => void;
  variant?: "default" | "project-registration";
}

const AuditModal = ({
  isOpen,
  onClose,
  label,
  area,
  onImport,
  variant = "default",
}: RequesterModalProps) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const projectId = searchParams.get("projectId") || "";
  const isProjectRegistrationAudit = variant === "project-registration";

  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedAction, setSelectedAction] = useState<string>("");
  const { data: usersData } = useAllUsers();

  const { data: auditData, isLoading, isError } = useQuery({
    queryKey: ["audit-data", area, projectId, selectedUser, selectedAction],
    queryFn: () =>
      AuditService.getByProjectAndArea({
        project: projectId,
        area,
        user: isProjectRegistrationAudit ? null : selectedUser || null,
        action: selectedAction || undefined,
      }),
    enabled: !!projectId && isOpen,
  });

  const selectedUserName = useMemo(() => {
    if (!selectedUser || !usersData) return null;
    const user = usersData.find((u: any) => u._id === selectedUser);
    return user?.name || null;
  }, [selectedUser, usersData]);

  const handleImport = () => {
    if (!onImport) return;
    onImport(auditData || []);
  };

  return (
    <Dialog
      onClose={onClose}
      open={isOpen}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          maxHeight: isProjectRegistrationAudit ? "94vh" : "90vh",
          height: isProjectRegistrationAudit ? { xs: "88vh", md: "92vh" } : "auto",
          width: isProjectRegistrationAudit
            ? { xs: "94vw", md: "980px" }
            : { xs: "90vw", md: "840px" },
          display: "flex",
          flexDirection: "column",
        },
      }}
      aria-modal="true"
      role="dialog"
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 4,
          py: 2.5,
          color: "#1361A4",
        }}
      >
        <Typography variant="h6" color="#1361A4" fontWeight="bold">
          Auditoria - {label}
        </Typography>
        <IconButton aria-label="Fechar" onClick={onClose} sx={{ color: "#666" }}>
          <IoMdClose size={24} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 4,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflow: "hidden",
          ...(isProjectRegistrationAudit
            ? { flex: "1 1 auto", minHeight: 0, maxHeight: "none" }
            : { maxHeight: "70vh" }),
        }}
      >
        {!isProjectRegistrationAudit && (
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="body2" color="textSecondary">
              Usuário
            </Typography>
            <Autocomplete
              size="small"
              sx={{ minWidth: 220, maxWidth: 320 }}
              options={(usersData || []).filter((user: any) => user.active)}
              getOptionLabel={(option: any) => option.name || ""}
              value={(usersData || []).find((u: any) => u._id === selectedUser) || null}
              onChange={(_, value) => {
                setSelectedUser(value?._id || null);
              }}
              clearOnEscape
              renderInput={(params) => (
                <TextField {...params} placeholder="Todos os usuários" variant="outlined" />
              )}
              noOptionsText="Nenhum usuário encontrado"
              isOptionEqualToValue={(option, value) => option._id === value._id}
            />

            {selectedUser && (
              <Chip
                label={selectedUserName}
                onDelete={() => setSelectedUser(null)}
                deleteIcon={<IoMdClose />}
                sx={{
                  bgcolor: "#E3F2FD",
                  color: "#1361A4",
                  "& .MuiChip-deleteIcon": { color: "#1361A4" },
                }}
              />
            )}
          </Box>
        )}

        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="body2" color="textSecondary">
            Tipo de evento
          </Typography>
          <TextField
            select
            size="small"
            sx={{ minWidth: 220 }}
            value={selectedAction}
            onChange={(e) => setSelectedAction(e.target.value)}
            SelectProps={{ displayEmpty: true }}
          >
            {AUDIT_EVENT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {isError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Erro ao carregar dados de auditoria. Tente novamente.
          </Alert>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <ChangesList
            data={auditData || []}
            isLoading={isLoading}
            isError={isError}
            selectedUserName={selectedUserName}
            differentiateEvents={area === "project"}
            fillParent={isProjectRegistrationAudit}
          />
        </Box>
      </DialogContent>

      {!isProjectRegistrationAudit && (
        <DialogActions sx={{ px: 4, py: 2, borderTop: "1px solid #E0E0E0" }}>
          <Button variant="outlined" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={isLoading || isError || !(auditData && auditData.length)}
          >
            Importar
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AuditModal;
